import React, { useEffect, useState } from 'react'
import { Button, Container, Modal, Spinner } from 'react-bootstrap'
import { LuGraduationCap } from 'react-icons/lu'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { useSidebar } from './SidebarContext'
import { isBusAttendantUser } from '../utils/busAttendant'
import {
  deleteAnnouncementRequest,
  fetchTopAnnouncements,
  getAnnouncementErrorMessage,
  parseAnnouncementListResponse,
} from './commonComponents/announcementApi'

export default function ParentTopAnnouncements() {
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name: instituteName } = useInstitute()
  const isParentUser = user?.user_type === 'parent' || user?.user_type === 'parents'
  const isAttendantUser = isBusAttendantUser(user)
  const showCreateAnnouncementButton =
    user?.user_type !== 'teacher' && user?.user_type !== 'parents' && !isAttendantUser && user?.user_type !== 'staff'
  const [announcements, setAnnouncements] = useState([])
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await fetchTopAnnouncements()
        if (cancelled) return
        setAnnouncements(parseAnnouncementListResponse(res))
      } catch (err) {
        if (!cancelled) {
          setAnnouncements([])
          toast.error(getAnnouncementErrorMessage(err, navigate))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const rowId = (r) => r.id ?? r.announcement_id

  const handleDelete = async () => {
    if (!deleteTarget?.id) return
    setDeleting(true)
    try {
      await deleteAnnouncementRequest(deleteTarget.id)
      setAnnouncements((prev) => prev.filter((r) => rowId(r) !== deleteTarget.id))
      setDeleteTarget(null)
      toast.success('Announcement deleted.')
    } catch (err) {
      toast.error(getAnnouncementErrorMessage(err, navigate))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
        `}
      </style>
      <div
        style={{
          ...(isParentUser
            ? {
                marginLeft,
                transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'margin-left',
                WebkitTransition:
                  'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                zIndex: 1,
                marginTop: '0px',
                marginRight: '0px',
                marginBottom: '0px',
                paddingLeft: isCollapsed ? '62px' : '12px',
                paddingRight: isCollapsed ? '62px' : '12px',
              }
            : {
                marginLeft,
                transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'margin-left',
                position: 'relative',
                zIndex: 1,
                marginTop: '0px',
                paddingLeft: '12px',
                paddingRight: '12px',
              }),
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: isMobile ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          {isParentUser ? (
            <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: '#F3F4F6',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  overflow: 'visible',
                  borderBottom: '1px solid #D1D3D4',
                  // width: '100%',
                }}
              >
                <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {!logoError ? (
                      <img
                        src={logo}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>

                  <div>
                    <h3
                      className="m-0 fw-bold text-start"
                      style={{
                        letterSpacing: '-0.02em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'clamp(18px, 2.5vw, 22px)',
                        color: '#223843',
                        lineHeight: '1.2',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Announcements
                    </h3>
                    <p
                      className="m-0 mt-1 text-start"
                      style={{
                        fontSize: window.innerWidth < 768 ? '11px' : '13px',
                        color: '#6b7280',
                        fontWeight: '400',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {instituteName || 'Institute'}
                    </p>
                  </div>
                </div>

                {!isMobile && <ProfileDropdown logout={logout} />}
              </div>
            </div>
          ) : (
            <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: '#F3F4F6',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #D1D3D4',
                  width: '100%',
                  gap: '12px',
                }}
              >
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {!logoError ? (
                      <img
                        src={logo}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3
                      className="m-0 fw-bold page-title text-start"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                        lineHeight: '1.2',
                      }}
                    >
                      Announcements
                    </h3>
                    <p
                      className="m-0 mt-0 text-start"
                      style={{
                        fontSize: isMobile ? '10px' : '13px',
                        color: '#6b7280',
                      }}
                    >
                      {instituteName || 'Institute'}
                    </p>
                  </div>
                </div>

                {!isMobile && (
                  <div
                    className="d-flex align-items-center gap-3"
                    style={{ justifyContent: 'flex-end' }}
                  >
                    <ProfileDropdown
                      logout={logout}
                      notificationCount={3}
                      showNotifications={true}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Container
          fluid
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '12px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-end mb-3">
          {showCreateAnnouncementButton && (
            <button
              type="button"
              className="d-none d-md-flex align-items-center justify-content-center"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '650',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Create New Announcement"
            >
              Create New Announcement
            </button>
            )}
          </div>

          <div className="d-md-none d-flex justify-content-end mb-3">
          {showCreateAnnouncementButton && (
            <button
              type="button"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '650',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Create New Announcement"
            >
              Create New Announcement
            </button>
            )}
          </div>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : announcements.length === 0 ? (
            <div
              className="text-center text-muted py-5 px-3"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
              }}
            >
              No announcements yet.
            </div>
          ) : (
            <div className="d-flex flex-column" style={{ gap: '16px' }}>
              {announcements.map((r, index) => {
                const id = rowId(r)
                const title = r.title || '—'
                const msg = r.message ?? r.description ?? '—'
                return (
                  <div
                    key={id ?? index}
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      padding: '18px 20px',
                      textAlign: 'left',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#6b7280',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        Title
                      </div>
                      <h5
                        className="m-0"
                        style={{
                          fontSize: '16px',
                          fontWeight: 650,
                          color: '#223843',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.35,
                        }}
                      >
                        {title}
                      </h5>
                    </div>
                    <div className="mt-3">
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#6b7280',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        Message
                      </div>
                      <p
                        className="m-0"
                        style={{
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#4b5563',
                          lineHeight: 1.55,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg || '—'}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Container>
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget
            ? `Delete "${deleteTarget.title}"? This cannot be undone.`
            : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
